/**
 * A3 退款管理页 — 双人审批
 *
 * Stream 2 Phase 2-2b / Plan §10 Stream 2 / Design §5.2 A3 退款双人审批.
 *
 * 功能：
 *  - 时效公示 Banner（必显）：7-15 个工作日到账告知
 *  - 顶部操作栏：新建退款按钮（REFUND_INITIATE）+ 多维度筛选
 *  - antd Table 展示退款列表（分页）
 *  - 行操作：pending_approval → 审批通过/拒绝；approved → 执行退款；任何状态 → 查看详情
 *  - 发起/审批通过/拒绝/执行 4 个 Modal
 *  - 详情 Drawer（展示完整字段含 3 个 note）
 *  - 整页用 PermissionGate REFUND_INITIATE | REFUND_APPROVE | REFUND_EXECUTE 保护；
 *    无任意退款权限则显示 403（退款模块统一入口用 REFUND_INITIATE 做页面级守卫）
 *
 * 权限：refund:initiate（super_admin / finance）/ refund:approve / refund:execute。
 */
import { useState } from 'react';
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Tag,
  Select,
  Result,
  Card,
  Alert,
  Tooltip,
  Typography,
  Modal,
  Drawer,
  Descriptions,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { InitiateRefundModal } from '../../components/InitiateRefundModal';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listRefunds,
  getRefund,
  approveRefund,
  rejectRefund,
  executeRefund,
  type RefundListItem,
  type RefundDetail,
  type RefundStatus,
  type ListRefundsFilter,
} from '../../api/refunds';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const REFUND_STATUS_OPTIONS: { value: RefundStatus | ''; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'pending_approval', label: '待审批' },
  { value: 'approved', label: '已审批' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'executed', label: '已执行' },
  { value: 'failed', label: '执行失败' },
];

function refundStatusColor(status: RefundStatus): string {
  switch (status) {
    case 'pending_approval': return 'blue';
    case 'approved':         return 'gold';
    case 'rejected':         return 'red';
    case 'executed':         return 'green';
    case 'failed':           return 'purple';
    default:                 return 'default';
  }
}

function refundStatusLabel(status: RefundStatus): string {
  switch (status) {
    case 'pending_approval': return '待审批';
    case 'approved':         return '已审批';
    case 'rejected':         return '已拒绝';
    case 'executed':         return '已执行';
    case 'failed':           return '执行失败';
    default:                 return status;
  }
}

/** 分转元，保留 2 位小数 */
function centToYuan(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/** ISO 日期 → YYYY-MM-DD HH:mm */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ---------------------------------------------------------------------------
// Modal: 审批通过
// ---------------------------------------------------------------------------

interface ApproveModalProps {
  open: boolean;
  refundId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ApproveRefundModal({ open, refundId, onClose, onSuccess }: ApproveModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return approveRefund(refundId!, { approvalNote: v.approvalNote || undefined });
    },
    onSuccess: () => {
      message.success('退款已审批通过');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '审批失败');
    },
  });

  return (
    <Modal
      title="审批通过"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => mutation.mutate()}
      confirmLoading={mutation.isPending}
      okText="确认通过"
      data-testid="approve-refund-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="approvalNote" label="审批备注（可选）">
          <TextArea rows={3} placeholder="对执行人的说明（可不填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: 拒绝退款
// ---------------------------------------------------------------------------

interface RejectModalProps {
  open: boolean;
  refundId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function RejectRefundModal({ open, refundId, onClose, onSuccess }: RejectModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return rejectRefund(refundId!, { rejectReason: v.rejectReason });
    },
    onSuccess: () => {
      message.success('退款申请已拒绝');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '拒绝操作失败');
    },
  });

  return (
    <Modal
      title="拒绝退款"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate());
      }}
      confirmLoading={mutation.isPending}
      okText="确认拒绝"
      okButtonProps={{ danger: true }}
      data-testid="reject-refund-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="rejectReason"
          label="拒绝原因"
          rules={[
            { required: true, message: '请输入拒绝原因' },
            { min: 5, message: '拒绝原因不得少于 5 个字符' },
          ]}
        >
          <TextArea rows={3} placeholder="请详细说明拒绝原因（不少于 5 字）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal: 执行退款
// ---------------------------------------------------------------------------

interface ExecuteModalProps {
  open: boolean;
  refundId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ExecuteRefundModal({ open, refundId, onClose, onSuccess }: ExecuteModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return executeRefund(refundId!, { executionNote: v.executionNote || undefined });
    },
    onSuccess: () => {
      message.success('退款已发起，等待微信支付处理');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '执行退款失败');
    },
  });

  return (
    <Modal
      title="执行退款"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => mutation.mutate()}
      confirmLoading={mutation.isPending}
      okText="确认执行"
      okButtonProps={{ type: 'primary', danger: true }}
      data-testid="execute-refund-modal"
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="操作不可撤销"
        description="确认执行此退款？退款将立即通过微信支付 v3 原路退回，不可撤销。预计 7-15 个工作日到账。"
        data-testid="execute-warning-alert"
      />
      <Form form={form} layout="vertical">
        <Form.Item name="executionNote" label="执行备注（可选）">
          <TextArea rows={2} placeholder="执行说明（可不填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Drawer: 退款详情
// ---------------------------------------------------------------------------

interface DetailDrawerProps {
  open: boolean;
  refundId: string | null;
  onClose: () => void;
}

function RefundDetailDrawer({ open, refundId, onClose }: DetailDrawerProps) {
  const query = useQuery({
    queryKey: ['refund-detail', refundId],
    queryFn: () => getRefund(refundId!),
    enabled: open && refundId != null,
  });

  const d: RefundDetail | undefined = query.data;

  return (
    <Drawer
      title="退款详情"
      open={open}
      onClose={onClose}
      width={640}
      data-testid="refund-detail-drawer"
    >
      {query.isLoading && <Text>加载中…</Text>}
      {d && (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="退款 ID">{d.id}</Descriptions.Item>
          <Descriptions.Item label="支付记录 ID">{d.paymentRecordId}</Descriptions.Item>
          <Descriptions.Item label="订单 ID">{d.orderId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="作品 ID">{d.workId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="用户 ID">{d.userId}</Descriptions.Item>
          <Descriptions.Item label="退款金额">{centToYuan(d.refundAmount)}</Descriptions.Item>
          <Descriptions.Item label="退款原因">{d.reason}</Descriptions.Item>
          <Descriptions.Item label="是否替支付">
            {d.isSubstitutePayment ? <Tag color="red">是</Tag> : '否'}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={refundStatusColor(d.status)}>{refundStatusLabel(d.status)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="发起人">{d.initiatorId}</Descriptions.Item>
          <Descriptions.Item label="发起时间">{formatDateTime(d.initiatedAt)}</Descriptions.Item>
          <Descriptions.Item label="发起备注">{d.initiatorNote ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="审批人">{d.approverId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="审批时间">
            {d.approvedAt ? formatDateTime(d.approvedAt) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="审批备注">{d.approvalNote ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="拒绝原因">{d.rejectReason ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行人">{d.executorId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行时间">
            {d.executedAt ? formatDateTime(d.executedAt) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="执行备注">{d.executionNote ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="微信退款单号">{d.outRefundNo ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行失败原因">
            {d.executionFailureReason ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDateTime(d.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatDateTime(d.updatedAt)}</Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Filter form
// ---------------------------------------------------------------------------

interface FilterFormValues {
  status?: RefundStatus | '';
  initiatorId?: string;
  approverId?: string;
  executorId?: string;
  isSubstitutePayment?: 'true' | 'false' | '';
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

// ---------------------------------------------------------------------------
// Main page (inner, after permission check)
// ---------------------------------------------------------------------------

function RefundsPageInner() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<FilterFormValues>();
  const [filter, setFilter] = useState<ListRefundsFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal state
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [executeId, setExecuteId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['refunds', filter, page, pageSize],
    queryFn: () => listRefunds({ ...filter, page, pageSize }),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListRefundsFilter = {};
    if (v.status) next.status = v.status as RefundStatus;
    if (v.initiatorId?.trim()) next.initiatorId = v.initiatorId.trim();
    if (v.approverId?.trim()) next.approverId = v.approverId.trim();
    if (v.executorId?.trim()) next.executorId = v.executorId.trim();
    if (v.isSubstitutePayment === 'true') next.isSubstitutePayment = true;
    if (v.isSubstitutePayment === 'false') next.isSubstitutePayment = false;
    if (v.dateRange?.[0]) next.fromDate = v.dateRange[0].toISOString();
    if (v.dateRange?.[1]) next.toDate = v.dateRange[1].toISOString();
    setFilter(next);
    setPage(1);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilter({});
    setPage(1);
  }

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['refunds'] });
  }

  // Table columns
  const columns: ColumnsType<RefundListItem> = [
    {
      title: '退款 ID',
      dataIndex: 'id',
      key: 'id',
      width: 180,
      render: (id: string) => (
        <Button
          type="link"
          style={{ padding: 0 }}
          onClick={() => setDetailId(id)}
          data-testid={`refund-id-${id}`}
        >
          <Tooltip title={id}>
            <Text ellipsis style={{ maxWidth: 160 }}>
              {id}
            </Text>
          </Tooltip>
        </Button>
      ),
    },
    {
      title: '支付记录 ID',
      dataIndex: 'paymentRecordId',
      key: 'paymentRecordId',
      width: 160,
      render: (id: string) => (
        <Tooltip title={id}>
          <Text ellipsis style={{ maxWidth: 140, display: 'block' }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '退款金额',
      dataIndex: 'refundAmount',
      key: 'refundAmount',
      width: 100,
      render: (v: number) => centToYuan(v),
    },
    {
      title: '退款原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 160,
      render: (r: string) => (
        <Tooltip title={r}>
          <Text ellipsis style={{ maxWidth: 140, display: 'block' }}>
            {r}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: RefundStatus) => (
        <Tag color={refundStatusColor(s)}>{refundStatusLabel(s)}</Tag>
      ),
    },
    {
      title: '替支付',
      dataIndex: 'isSubstitutePayment',
      key: 'isSubstitutePayment',
      width: 80,
      render: (v: boolean) =>
        v ? <Tag color="red">是</Tag> : <Text type="secondary">否</Text>,
    },
    {
      title: '发起人',
      dataIndex: 'initiatorId',
      key: 'initiatorId',
      width: 140,
      ellipsis: true,
    },
    {
      title: '审批人',
      dataIndex: 'approverId',
      key: 'approverId',
      width: 140,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '执行人',
      dataIndex: 'executorId',
      key: 'executorId',
      width: 140,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, row: RefundListItem) => (
        <Space size="small">
          {/* pending_approval → 审批通过 + 拒绝 */}
          {row.status === 'pending_approval' && (
            <PermissionGate point={PERMISSIONS.REFUND_APPROVE}>
              <>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setApproveId(row.id)}
                  data-testid={`approve-btn-${row.id}`}
                >
                  审批通过
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => setRejectId(row.id)}
                  data-testid={`reject-btn-${row.id}`}
                >
                  拒绝
                </Button>
              </>
            </PermissionGate>
          )}
          {/* approved → 执行退款 */}
          {row.status === 'approved' && (
            <PermissionGate point={PERMISSIONS.REFUND_EXECUTE}>
              <Button
                size="small"
                type="primary"
                danger
                onClick={() => setExecuteId(row.id)}
                data-testid={`execute-btn-${row.id}`}
              >
                执行退款
              </Button>
            </PermissionGate>
          )}
          {/* 任何状态 → 查看详情 */}
          <Button
            size="small"
            type="link"
            onClick={() => setDetailId(row.id)}
            data-testid={`detail-btn-${row.id}`}
          >
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <span>
          退款管理
          <Text type="secondary" style={{ fontSize: 13, marginLeft: 12, fontWeight: 400 }}>
            A3 双人审批 + 7-15 工作日时效
          </Text>
        </span>
      }
      data-testid="refunds-page"
    >
      {/* 时效公示 Banner（必显） */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="退款时效公示"
        description="退款将通过微信支付原路退回至付款人微信账户，到账时间预计 7-15 个工作日。请耐心等待，避免重复申请。"
        data-testid="refund-timeline-banner"
      />

      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <PermissionGate point={PERMISSIONS.REFUND_INITIATE}>
          <Button
            type="primary"
            onClick={() => setInitiateOpen(true)}
            data-testid="initiate-refund-btn"
          >
            新建退款
          </Button>
        </PermissionGate>

        <Form
          form={filterForm}
          layout="inline"
          style={{ flex: 1, flexWrap: 'wrap', gap: 8 }}
          data-testid="refunds-filter-form"
        >
          <Form.Item name="status" label="状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={REFUND_STATUS_OPTIONS.filter((o) => o.value !== '')}
            />
          </Form.Item>
          <Form.Item name="initiatorId" label="发起人 ID">
            <Input placeholder="initiatorId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="approverId" label="审批人 ID">
            <Input placeholder="approverId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="executorId" label="执行人 ID">
            <Input placeholder="executorId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="isSubstitutePayment" label="替支付">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 100 }}
              options={[
                { value: 'true', label: '是' },
                { value: 'false', label: '否' },
              ]}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={applyFilters}
                data-testid="refunds-search"
              >
                查询
              </Button>
              <Button onClick={resetFilters} data-testid="refunds-reset">
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* 退款列表表格 */}
      <Table<RefundListItem>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        scroll={{ x: 1600 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        data-testid="refunds-table"
      />

      {/* 发起退款 Modal */}
      <InitiateRefundModal
        open={initiateOpen}
        onClose={() => setInitiateOpen(false)}
        onSuccess={invalidate}
      />

      {/* 审批通过 Modal */}
      <ApproveRefundModal
        open={approveId != null}
        refundId={approveId}
        onClose={() => setApproveId(null)}
        onSuccess={invalidate}
      />

      {/* 拒绝 Modal */}
      <RejectRefundModal
        open={rejectId != null}
        refundId={rejectId}
        onClose={() => setRejectId(null)}
        onSuccess={invalidate}
      />

      {/* 执行 Modal */}
      <ExecuteRefundModal
        open={executeId != null}
        refundId={executeId}
        onClose={() => setExecuteId(null)}
        onSuccess={invalidate}
      />

      {/* 详情 Drawer */}
      <RefundDetailDrawer
        open={detailId != null}
        refundId={detailId}
        onClose={() => setDetailId(null)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Export: page-level PermissionGate (REFUND_INITIATE as gate; finance role)
// ---------------------------------------------------------------------------

export default function RefundsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.REFUND_INITIATE}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有访问退款管理的权限，请联系管理员申请 refund:initiate 权限。"
        />
      }
    >
      <RefundsPageInner />
    </PermissionGate>
  );
}
